// ============================================================
// AI Rådgiver — lånemodell for «Hva skjer hvis … jeg investerer»
// ============================================================
// Modellen regner en nedbetalingsplan, ikke et lånetilbud. Renten er brukerens eget
// anslag, og planen er den samme som scenariokurven bruker — teksten, tabellen og
// grafen kan derfor ikke si tre forskjellige ting om det samme lånet.
//
// To låneformer, fordi de gir helt ulik likviditetsprofil de første årene:
// - annuitet: fast terminbeløp, mest rente først
// - serielån: fast avdrag, høyest terminbeløp først og synkende
const PlummeAiAdvisorLoan = (() => {
  const num = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const TYPES = ['annuity', 'serial'];
  const normalizeType = (value) => (TYPES.includes(String(value)) ? String(value) : 'annuity');

  // Terminbeløpet for et annuitetslån. Med nullrente finnes ikke annuitetsformelen
  // (den deler på 0), og lånet er da rent avdrag.
  const annuityPayment = (principal, monthlyRate, termMonths) => (monthlyRate > 0
    ? principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -termMonths))
    : principal / termMonths);

  // Én rad per termin. Siste termin tar resten av restgjelden, slik at avrunding
  // underveis ikke etterlater noen kroner igjen på et nedbetalt lån.
  const buildSchedule = ({ principal, annualRatePct = 0, termMonths = 1, type = 'annuity' } = {}) => {
    const total = Math.max(0, num(principal));
    const months = Math.min(600, Math.max(1, Math.trunc(num(termMonths, 1))));
    const monthlyRate = Math.max(0, num(annualRatePct)) / 100 / 12;
    const loanType = normalizeType(type);
    const annuity = annuityPayment(total, monthlyRate, months);
    const rows = [];
    let remaining = total;
    for (let index = 0; index < months; index += 1) {
      const interest = remaining * monthlyRate;
      const wanted = loanType === 'serial' ? total / months : Math.max(0, annuity - interest);
      const principalPart = index === months - 1 ? remaining : Math.min(remaining, wanted);
      remaining = Math.max(0, remaining - principalPart);
      rows.push({
        index,
        payment: Math.round(principalPart + interest),
        interest: Math.round(interest),
        principal: Math.round(principalPart),
        remaining: Math.round(remaining),
      });
    }
    return rows;
  };

  const summarize = (rows) => {
    const list = Array.isArray(rows) ? rows : [];
    if (!list.length) return { totalInterest: 0, totalPaid: 0, firstPayment: 0, lastPayment: 0, months: 0 };
    return {
      totalInterest: list.reduce((sum, row) => sum + num(row.interest), 0),
      totalPaid: list.reduce((sum, row) => sum + num(row.payment), 0),
      firstPayment: num(list[0].payment),
      lastPayment: num(list[list.length - 1].payment),
      months: list.length,
    };
  };

  return { buildSchedule, summarize, normalizeType, TYPES };
})();

Object.assign(globalThis, { PlummeAiAdvisorLoan });
